import { RetailPartnerDetailFullDto } from "domain/payload/response/retailPartner/RetailPartnerResp";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import RetailPartnerService from "service/RetailPartnerService/RetailPartnerService";

interface RetailPartnerStoreDetailModalProps {
  show: boolean;
  storeId?: string;
  onClose: () => void;
}

const RetailPartnerStoreDetailModal: React.FC<RetailPartnerStoreDetailModalProps> = ({
  show,
  storeId,
  onClose,
}) => {
  const { profile } = useSelector((state: any) => state.auth);
  const retailPartnerService = RetailPartnerService();

  const [data, setData] = useState<RetailPartnerDetailFullDto | null>(null);
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(false);

  const allItems = data?.galleryList?.flatMap((group) =>
    group.items.map((item) => ({ ...item, category: group.name }))
  );

  const filteredImages =
    filter === "All"
      ? allItems
      : allItems?.filter((img) => img.category === filter);

  const fetchData = async () => {
    if (!storeId) return;

    const token = profile?.data.token;
    if (!token) return;

    setLoading(true);
    try {
      const res = await retailPartnerService.getDetail(token, storeId);
      setData(res.data ?? null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show && storeId) {
      fetchData();
    }
  }, [show, storeId]);

  useEffect(() => {
    if (!show) {
      setData(null);
      setFilter("All");
    }
  }, [show]);

  const handleWhatsAppClick = () => {
    if (!data?.ownerPhone) return;

    const cleanNumber = data.ownerPhone.replace(/\D/g, "");
    let whatsappNumber = cleanNumber;

    if (whatsappNumber.startsWith("0")) {
      whatsappNumber = "62" + whatsappNumber.substring(1);
    }

    window.open(`https://wa.me/${whatsappNumber}`, "_blank");
  };

  if (!show) return null;

  return (
    <div
      className="modal fade show"
      style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-xl modal-dialog-scrollable"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content position-relative">
          {loading && (
            <div
              className="position-absolute w-100 h-100 d-flex justify-content-center align-items-center"
              style={{ backgroundColor: "rgba(255,255,255,0.8)", zIndex: 9999 }}
            >
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}

          <div className="modal-header">
            <div>
              <h5 className="modal-title text-primary fw-bold mb-1">
                {data?.storeName}
              </h5>
              <p className="text-muted small mb-0">
                {data?.latitude}, {data?.longitude}
              </p>
            </div>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
              disabled={loading}
            ></button>
          </div>

          <div className="modal-body">
            {!loading && data && (
              <>
                <div className="row">
                  {/* Left Column - Store Info */}
                  <div className="col-lg-6 mb-4">
                    <div className="row g-4">
                      <div className="col-md-6">
                        <p className="text-muted small mb-1">Pemilik Toko</p>
                        <p className="fw-semibold mb-0">{data.ownerName || "-"}</p>
                      </div>
                      <div className="col-md-6">
                        <p className="text-muted small mb-1">Kontak</p>
                        <div className="d-flex align-items-center">
                          <p className="fw-semibold mb-0 me-2">
                            {data.ownerPhone || "-"}
                          </p>
                          {data.ownerPhone && (
                            <i
                              className="fa-brands fa-whatsapp text-success cursor-pointer"
                              style={{ cursor: "pointer", fontSize: "1.2rem" }}
                              onClick={handleWhatsAppClick}
                              title="Hubungi via WhatsApp"
                            ></i>
                          )}
                        </div>
                      </div>

                      <div className="col-md-4">
                        <p className="text-muted small mb-1">Store Type</p>
                        <p className="fw-semibold mb-0">{data.storeTypeName}</p>
                      </div>
                      <div className="col-md-4">
                        <p className="text-muted small mb-1">Road Type</p>
                        <p className="fw-semibold mb-0">{data.roadTypeName}</p>
                      </div>
                      <div className="col-md-4">
                        <p className="text-muted small mb-1">Years in Operation</p>
                        <p className="fw-semibold mb-0">{data.yearsInOperation}</p>
                      </div>

                      <div className="col-12">
                        <h6 className="fw-semibold mb-2">Address</h6>
                        <p className="text-muted small mb-3">{data.address || "-"}</p>
                        <div className="ratio ratio-16x9">
                          <iframe
                            src={`https://www.google.com/maps?q=${data.latitude},${data.longitude}&hl=id&z=16&output=embed`}
                            className="rounded"
                            style={{ border: 0 }}
                            loading="lazy"
                          ></iframe>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Gallery */}
                  <div className="col-lg-6 mb-4">
                    <h6 className="fw-bold text-primary mb-3">Gallery</h6>
                    
                    {/* Filter Buttons */}
                    <div className="d-flex gap-2 mb-3 flex-wrap">
                      <button
                        onClick={() => setFilter("All")}
                        className={`btn btn-sm rounded-pill ${
                          filter === "All"
                            ? "btn-primary"
                            : "btn-outline-secondary"
                        }`}
                      >
                        All
                      </button>
                      {data.galleryList?.map((group) => (
                        <button
                          key={group.id}
                          onClick={() => setFilter(group.name)}
                          className={`btn btn-sm rounded-pill ${
                            filter === group.name
                              ? "btn-primary"
                              : "btn-outline-secondary"
                          }`}
                        >
                          {group.name}
                        </button>
                      ))}
                    </div>

                    {/* Gallery Images */}
                    <div
                      className="row g-2"
                      style={{ maxHeight: "500px", overflowY: "auto" }}
                    >
                      {filteredImages && filteredImages.length > 0 ? (
                        filteredImages.map((img) => (
                          <div key={img.id} className="col-6">
                            <img
                              src={img.fileUrl}
                              alt={img.category}
                              className="w-100 rounded shadow-sm cursor-pointer hover-opacity-80"
                              style={{
                                height: "150px",
                                objectFit: "cover",
                                cursor: "pointer",
                              }}
                              onClick={() => window.open(img.fileUrl, "_blank")}
                            />
                          </div>
                        ))
                      ) : (
                        <div className="col-12 text-center py-4">
                          <i className="fa-solid fa-images text-muted fa-3x mb-2"></i>
                          <p className="text-muted">No images available</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="modal-footer border-top">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RetailPartnerStoreDetailModal;